import * as THREE from "three";

import type {
  LoomBooleanConstructionPoint,
  LoomBooleanConstructionSeam,
  LoomBooleanConstructionTrace,
} from "../../api/loomRebuildConstructionTrace";

export interface LoomBooleanConstructionTraceColors {
  prior: string;
  active: string;
  corrected: string;
}

export interface LoomBooleanConstructionTracePresentation {
  group: THREE.Group;
  seamCount: number;
  maximumUncertaintyM: number;
  displayUncertaintyRadiusM: number;
  advance: (elapsedMs: number) => boolean;
  positionSignature: () => string;
}

export function addLoomBooleanConstructionTracePresentation(
  parent: THREE.Group,
  trace: LoomBooleanConstructionTrace | null,
  bounds: THREE.Box3,
  colors: LoomBooleanConstructionTraceColors,
): LoomBooleanConstructionTracePresentation | null {
  if (!trace || bounds.isEmpty()) return null;
  const presentation = buildLoomBooleanConstructionTracePresentation(
    trace, colors, bounds.getSize(new THREE.Vector3()).length() || 1,
  );
  parent.add(presentation.group);
  return presentation;
}

export function loomBooleanConstructionTraceAnimator(
  presentation: LoomBooleanConstructionTracePresentation | null,
  startedAt: number,
): ((now: number) => boolean) | undefined {
  return presentation ? (now) => presentation.advance(now - startedAt) : undefined;
}

interface AnimatedSeam {
  active: THREE.LineLoop<THREE.BufferGeometry, THREE.LineBasicMaterial>;
  marker: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>;
  source: LoomBooleanConstructionSeam;
}

function vectors(points: readonly LoomBooleanConstructionPoint[]): THREE.Vector3[] {
  return points.map(([x, y, z]) => new THREE.Vector3(x, y, z));
}

function line(points: readonly LoomBooleanConstructionPoint[], material: THREE.Material): THREE.LineLoop {
  return new THREE.LineLoop(new THREE.BufferGeometry().setFromPoints(vectors(points)), material);
}

function smooth(value: number): number {
  const bounded = THREE.MathUtils.clamp(value, 0, 1);
  return bounded * bounded * (3 - 2 * bounded);
}

function interpolatedPoint(
  source: LoomBooleanConstructionSeam,
  index: number,
  elapsedMs: number,
): LoomBooleanConstructionPoint {
  const first = elapsedMs < 900;
  const from = first ? source.previous_points_m[index]! : source.predicted_points_m[index]!;
  const to = first ? source.predicted_points_m[index]! : source.corrected_points_m[index]!;
  const blend = smooth(first ? elapsedMs / 900 : (elapsedMs - 900) / 900);
  return [
    THREE.MathUtils.lerp(from[0], to[0], blend),
    THREE.MathUtils.lerp(from[1], to[1], blend),
    THREE.MathUtils.lerp(from[2], to[2], blend),
  ];
}

function updateAnimatedSeam(seam: AnimatedSeam, elapsedMs: number): void {
  const attribute = seam.active.geometry.getAttribute("position") as THREE.BufferAttribute;
  for (let index = 0; index < seam.source.previous_points_m.length; index += 1) {
    const [x, y, z] = interpolatedPoint(seam.source, index, elapsedMs);
    attribute.setXYZ(index, x, y, z);
  }
  attribute.needsUpdate = true;
  seam.active.geometry.computeBoundingSphere();
  const markerIndex = Math.floor((elapsedMs / 90) % seam.source.corrected_points_m.length);
  const [x, y, z] = interpolatedPoint(seam.source, markerIndex, elapsedMs);
  seam.marker.position.set(x, y, z);
}

export function buildLoomBooleanConstructionTracePresentation(
  trace: LoomBooleanConstructionTrace,
  colors: LoomBooleanConstructionTraceColors,
  modelScale: number,
): LoomBooleanConstructionTracePresentation {
  const group = new THREE.Group();
  group.name = "Provisional Boolean construction trace";
  group.renderOrder = 70;
  group.userData.loomConstructionTraceAuthority = trace.authority;
  const maximumUncertaintyM = Math.max(...trace.seams.flatMap((seam) => seam.uncertainty_radius_m));
  const displayUncertaintyRadiusM = Math.min(
    Math.max(maximumUncertaintyM * 8, modelScale * 0.002), modelScale * 0.012,
  );
  const animated: AnimatedSeam[] = [];
  for (const seam of trace.seams) {
    const uncertainty = new THREE.Mesh(
      new THREE.TubeGeometry(
        new THREE.CatmullRomCurve3(vectors(seam.predicted_points_m), true, "centripetal"),
        seam.predicted_points_m.length * 3, displayUncertaintyRadiusM, 8, true,
      ),
      new THREE.MeshBasicMaterial({
        color: colors.active, depthTest: false, depthWrite: false, opacity: 0.22,
        side: THREE.DoubleSide, transparent: true,
      }),
    );
    uncertainty.name = `${seam.side} exaggerated uncertainty envelope`;
    uncertainty.renderOrder = 70;
    const corrected = line(seam.corrected_points_m, new THREE.LineBasicMaterial({
      color: colors.corrected, depthTest: false, depthWrite: false, opacity: 0.9, transparent: true,
    }));
    corrected.name = `${seam.side} corrected seam target`;
    corrected.renderOrder = 72;
    const activeGroup = new THREE.Group();
    const active = line(seam.previous_points_m, new THREE.LineBasicMaterial({
      color: colors.active, depthTest: false, depthWrite: false, transparent: true,
    })) as THREE.LineLoop<THREE.BufferGeometry, THREE.LineBasicMaterial>;
    active.name = `${seam.side} marching seam`;
    active.renderOrder = 74;
    const marker = new THREE.Mesh(
      new THREE.SphereGeometry(displayUncertaintyRadiusM * 1.25, 10, 7),
      new THREE.MeshBasicMaterial({ color: colors.prior, depthTest: false, depthWrite: false }),
    );
    marker.name = `${seam.side} marching marker`;
    marker.renderOrder = 75;
    activeGroup.add(active, marker);
    group.add(uncertainty, corrected, activeGroup);
    animated.push({ active, marker, source: seam });
  }
  const advance = (elapsedMs: number) => {
    animated.forEach((seam) => updateAnimatedSeam(seam, Math.max(0, elapsedMs)));
    return true;
  };
  const positionSignature = () => animated.map(({ active }) =>
    Array.from((active.geometry.getAttribute("position") as THREE.BufferAttribute).array)
      .slice(0, 9).map((value) => Number(value).toFixed(5)).join(":"),
  ).join("|");
  return {
    group, seamCount: trace.seams.length, maximumUncertaintyM,
    displayUncertaintyRadiusM, advance, positionSignature,
  };
}
